import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Layout, Menu, Dropdown, Typography, Avatar, Space, Badge, Select, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, BellOutlined, SettingOutlined, ApartmentOutlined } from '@ant-design/icons';
import axios from 'axios';

const url_base = process.env.REACT_APP_URL_BACKEND;
const { Header } = Layout;
const { Text } = Typography;

const AppHeader = () => {
    const token = localStorage.getItem('token');
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const roles = user?.role?.split(',').map(r => r.trim()) || [];
    const isAdmin = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_DEV') || roles.includes('ADMIN');
    // Super-admin = sem empresaClienteId (vê todas as empresas)
    const isSuperAdmin = isAdmin && !user?.empresaClienteId;

    const [empName, setEmpName] = useState(() => {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return u?.empresaClienteNome || localStorage.getItem('companyName') || 'Heliosfera';
    });
    const [empId, setEmpId] = useState(() => {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return u?.empresaClienteId || localStorage.getItem('companyId') || null;
    });
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [notifications] = useState([]);

    // Sync empName quando o user muda (ex: depois do login)
    useEffect(() => {
        if (user?.empresaClienteNome) {
            setEmpName(user.empresaClienteNome);
            setEmpId(user.empresaClienteId);
        }
    }, [user?.empresaClienteNome, user?.empresaClienteId]);

    // Super-admin: buscar lista real de empresas da API
    useEffect(() => {
        if (!isSuperAdmin) return;
        setLoadingCompanies(true);
        axios.get(`${url_base}/api/usuarios/empresas`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : [];
                setCompanies(data);
                // Default: Heliosfera (sem empresaClienteId = nenhuma empresa específica)
                if (!empId && !empName) setEmpName('Heliosfera');
            })
            .catch(() => {
                setCompanies([]);
            })
            .finally(() => setLoadingCompanies(false));
    }, [isSuperAdmin, token]);

    const handleCompanyChange = (value, option) => {
        if (value === '__heliosfera__') {
            setEmpName('Heliosfera');
            setEmpId(null);
            localStorage.setItem('companyName', 'Heliosfera');
            localStorage.removeItem('companyId');
        } else {
            setEmpName(option.label);
            setEmpId(value);
            localStorage.setItem('companyName', option.label);
            localStorage.setItem('companyId', value);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getAvatarText = (name) => {
        if (!name) return 'H';
        return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
    };

    const notificationsMenu = (
        <Menu style={{ padding: '16px', minWidth: 200 }}>
            <Text type="secondary">Sem notificações</Text>
        </Menu>
    );

    const userMenu = (
        <Menu>
            <Menu.Item key="account" icon={<UserOutlined />} onClick={() => navigate('/gerenciar-conta')}>
                Conta
            </Menu.Item>
            {isAdmin && (
                <Menu.Item key="manage-users" icon={<SettingOutlined />} onClick={() => navigate('/gerenciar-usuarios')}>
                    Gerenciar Usuários
                </Menu.Item>
            )}
            <Menu.Divider />
            <Menu.Item key="logout" onClick={handleLogout} style={{ color: '#ff4d4f' }}>
                Sair
            </Menu.Item>
        </Menu>
    );

    return (
        <Header style={{
            backgroundColor: '#fff',
            paddingRight: 24,
            paddingLeft: 24,
            borderBottom: '1px solid #f0f0f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>

                {/* ── Esquerda: empresa ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar size={36} style={{ backgroundColor: '#7265e6', flexShrink: 0 }}>
                        {getAvatarText(empName)}
                    </Avatar>

                    {isSuperAdmin ? (
                        /* Admin global: dropdown para trocar de empresa */
                        <Spin spinning={loadingCompanies} size="small">
                            <Select
                                value={empId || '__heliosfera__'}
                                onChange={handleCompanyChange}
                                style={{ minWidth: 180 }}
                                bordered={false}
                                suffixIcon={<ApartmentOutlined style={{ color: '#888' }} />}
                                options={[
                                    { value: '__heliosfera__', label: 'Heliosfera (Global)' },
                                    ...companies.map(c => ({ value: c.id, label: c.nome })),
                                ]}
                                dropdownMatchSelectWidth={false}
                            />
                        </Spin>
                    ) : (
                        /* Cliente / admin de empresa: só exibe o nome */
                        <Text style={{ fontSize: 14, color: '#444', fontWeight: 500 }}>
                            {empName}
                        </Text>
                    )}
                </div>

                {/* ── Direita: notificações + usuário ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
                    <Dropdown overlay={notificationsMenu} trigger={['click']}>
                        <Badge count={notifications.length} offset={[8, 0]}>
                            <BellOutlined style={{ fontSize: 20, cursor: 'pointer', color: '#555' }} />
                        </Badge>
                    </Dropdown>

                    <Dropdown overlay={userMenu} trigger={['click']}>
                        <Space style={{ cursor: 'pointer' }}>
                            <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}>
                                <Text style={{ fontSize: 13 }}>{user?.username}</Text>
                                <Text style={{ fontSize: 11, color: '#888' }}>
                                    {roles.includes('ROLE_ADMIN') || roles.includes('ADMIN')
                                        ? 'Administrador'
                                        : roles.includes('ROLE_DEV')
                                            ? 'Desenvolvedor'
                                            : 'Cliente'}
                                </Text>
                            </div>
                        </Space>
                    </Dropdown>
                </div>
            </div>
        </Header>
    );
};

export default AppHeader;
